import React, { useEffect, useRef, useState } from "react";

import { Button, Modal, Row } from "react-bootstrap";

import classNames from "classnames";

import styles from "./TermsAndConditionsModal.module.scss";

interface TermsAndConditionsModalProps {
  isOpen: boolean;
  closeModal: () => void;
}

const TermsAndConditionsModal = ({ isOpen, closeModal }: TermsAndConditionsModalProps) => {
  const [isAgreed, setIsAgreed] = useState<boolean>(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      // Allow user even if they do not scroll the last 5 px which is included in the bottom margin anyways, thus ensuring they have scrolled until the last line.
      if (scrollTop + clientHeight >= scrollHeight - 5) {
        setIsAgreed(true);
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAgreed(false);
    }
  }, [isOpen, setIsAgreed]);

  return (
    <Modal show={isOpen} onHide={closeModal} centered size="lg" backdrop="static">
      <Modal.Header>
        <Modal.Title>Please read and agree</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row
          className="d-flex justify-content-center align-items-center"
          ref={contentRef}
          onScroll={handleScroll}
          style={{ maxHeight: "300px", overflowY: "auto" }}
        >
          <Row className={classNames(styles["title"], "mb-3")}>User agreement</Row>
          <Row className="b3">
            <ol className={classNames(styles["main-list"])}>
              <li>
                <span className="b3 bold">Acceptance of these Terms</span>

                <ol className={classNames(styles["sub-list"])}>
                  <li>
                    Copyright Agency Ltd (ABN 53 001 228 799) (<span className="b3 bold">we</span>,{" "}
                    <span className="b3 bold">us</span> or <span className="b3 bold">our</span>) own or license the
                    Resale Royalty Platform software and the information it contains{" "}
                    <span className="b3 bold">(Resale Royalty Platform)</span>.
                  </li>
                  <li>These terms and conditions (Terms) apply to:</li>
                  <ol className={classNames(styles["sub-sub-list"])}>
                    <li>
                      the individual, company, entity or organisation who is entering into a legal agreement with us to
                      be granted access to use the Resale Royalty Platform for the purpose of the Resale Royalty Scheme
                      as an artist, a beneficiary, an art market professional, an art buyer or an art seller and for the
                      purpose of reporting a resale by an art seller, an art buyer or an art market professional; and
                    </li>
                    <li>
                      to the Personnel of a company, entity or organisation who access or use the Resale Royalty
                      Platform <span className="b3 bold">(User Account)</span>
                    </li>
                    (jointly and severally known as <span className="b3 bold">you </span>or{" "}
                    <span className="b3 bold">your</span>).
                    <br />
                    <br />
                  </ol>
                  <li>
                    You accept these Terms by clicking the “I agree and register” button or by using or accessing the
                    Resale Royalty Platform.
                  </li>
                  <li>
                    These Terms will commence on the date on which your User Account is created and will continue unless
                    terminated in accordance with clause 8 (the <span className="b3 bold">Term</span>).
                  </li>
                  <li>
                    By accepting these Terms, or accessing and/or using the Resale Royalty Platform, you warrant to us
                    that you have the legal capacity to enter into a legally binding agreement.
                  </li>
                  <li>
                    If you are entering into these Terms on behalf of your employer or a business entity, you, in your
                    individual capacity, represent and warrant that you are authorised to act on behalf of your employer
                    or the business entity and to bind the entity and the entity’s personnel to these Terms.
                  </li>
                </ol>
              </li>
              <li>
                <span className="b3 bold">Use restrictions</span>
                <ol className={classNames(styles["sub-list"])}>
                  <li>
                    Subject to your compliance with these Terms, we grant you a non-exclusive, non-transferable,
                    non-sublicensable, personal and revocable licence to access and use the Resale Royalty Platform for
                    the Term for the purpose described in clause 1.2(a) above <span className="b3 bold">(Purpose)</span>
                    , and in the manner set out on the Resale Royalty Platform{" "}
                    <span className="b3 bold">(Licence)</span>.
                  </li>
                  <li>
                    You must not access or use the Resale Royalty Platform except as permitted by these Terms and as set
                    out on the Resale Royalty Platform. Without limiting the foregoing, you must not and must not permit
                    any other person to:
                    <ol className={classNames(styles["sub-sub-list"])}>
                      <li>
                        use the Resale Royalty Platform in any way which is in breach of any applicable Laws or which
                        infringes any person&apos;s rights, including Intellectual Property Rights and privacy rights;
                      </li>
                      <li>
                        use the Resale Royalty Platform in any way that damages, interferes with or interrupts the
                        supply of the Resale Royalty Platform or our systems;
                      </li>
                      <li>
                        compromise the physical and administrative security of the Resale Royalty Platform, including by
                        allowing others to access your/their account, password or authentication details;
                      </li>
                      <li>
                        use the Resale Royalty Platform to carry out security breaches or disruptions of a network;
                      </li>
                      <li>
                        decompile, disassemble, reverse engineer, attempt to derive the source code for, modify, adapt,
                        translate or copy the Resale Royalty Platform;
                      </li>
                      <li>
                        provide, disclose, divulge, make available to or permit use of by, any part of the Resale
                        Royalty Platform or any information obtained on it by any third party; or
                      </li>
                      <li>
                        remove, tamper with, seek to override or otherwise interfere with any security or technological
                        protection measure forming part of the Resale Royalty Platform.
                      </li>
                    </ol>
                    This clause 2 will survive the expiry or termination of these Terms.
                  </li>
                </ol>
              </li>

              <li>
                <span className="b3 bold">Your warranties, acknowledgments and consents</span>
                <ol className={classNames(styles["sub-list"])}>
                  <li>
                    You warrant and agree that:
                    <ol className={classNames(styles["sub-sub-list"])}>
                      <li>you have reviewed and understand these Terms;</li>
                      <li>
                        you have or will acquire all hardware, software and capabilities which are necessary to access
                        and use the Resale Royalty Platform;
                      </li>
                      <li>you will use the Resale Royalty Platform in accordance with these Terms;</li>
                      <li>
                        all information and documentation that you provide to us in connection with these Terms is true,
                        correct and complete and you acknowledge and agree that we will rely on such information and
                        documentation in order to provide the Resale Royalty Platform and operate the Resale Royalty
                        Scheme; and
                      </li>
                      <li>
                        in entering into these Terms, you have not relied upon any representation, warranty or
                        inducement by us nor is any representation, warranty or thing made or done by us to be inferred,
                        incorporated or implied into these Terms.
                      </li>
                    </ol>
                  </li>
                  <li>
                    You acknowledge and agree that:
                    <ol className={classNames(styles["sub-list"])}>
                      <li>you are responsible for your use of the Resale Royalty Platform;</li>
                      <li>
                        you use the Resale Royalty Platform and any associated programs and files at your own risk;
                      </li>
                      <li>
                        the features of and information available on the Resale Royalty Platform may change from time to
                        time;
                      </li>
                      <li>
                        the provision of the Resale Royalty Platform may be contingent on, or impacted by, Third Party
                        Inputs and we do not make any warranty or representation in respect of any Third Party Inputs;
                      </li>
                      <li>
                        the Resale Royalty Platform may use third party products, facilities, services or data. We do
                        not make any warranty or representation in respect of the third-party products, facilities,
                        services or accuracy of the data;
                      </li>
                      <li>
                        you are solely responsible, on and from the time of export, for any files and/or documents
                        exported from the Resale Royalty Platform;
                      </li>
                      <li>
                        we are not responsible for the integrity or existence of any data on the Computing Environment,
                        network or any device controlled by you; and
                      </li>
                      <li>
                        the Resale Royalty Platform will be unavailable where required for maintenance, updates or other
                        changes. While we aim for the Resale Royalty Platform to be available at all other times, we do
                        not warrant that the Resale Royalty Platform is error-free or will be uninterrupted.
                      </li>
                    </ol>
                  </li>
                </ol>
              </li>
              <li>
                <span className="b3 bold">Intellectual Property Rights</span>
                <ol className={classNames(styles["sub-list"])}>
                  <li>
                    You agree that we own all Intellectual Property Rights in Our Materials, New Materials and
                    Improvements.
                  </li>
                  <li>
                    Without limitation to clause 2.2, you must not whether directly or indirectly, without our prior
                    written consent:
                    <ol className={classNames(styles["sub-sub-list"])}>
                      <li>
                        copy, modify, adapt, translate, create a derivative work of, reverse engineer, reverse assemble,
                        disassemble or decompile the Resale Royalty Platform or otherwise attempt to discover any part
                        of the source code of the Resale Royalty Platform; or
                      </li>
                      <li>
                        use or knowingly permit the use of any security testing tools in order to probe, scan or attempt
                        to penetrate or ascertain the security of the Resale Royalty Platform.
                      </li>
                    </ol>
                  </li>
                  <li>This clause 4 will survive the expiry or termination of these Terms.</li>
                </ol>
              </li>
              <li>
                <span className="b3 bold">Analytics</span>
                <ol className={classNames(styles["sub-list"])}>
                  <li>
                    Despite anything to the contrary in these Terms, we may monitor, analyse and compile information
                    based on and/or related to your use or the use of the Resale Royalty Platform, in an aggregated and
                    anonymised format (Analytics).
                  </li>
                  <li>
                    We and our licensors own all right, title and interest in and to the Analytics and all related
                    software, technology, documentation and content provided in connection with the Analytics, including
                    all Intellectual Property Rights in the foregoing.
                  </li>
                </ol>
              </li>
              <li>
                <span className="b3 bold">Privacy</span>
                <ol className={classNames(styles["sub-list"])}>
                  <li>
                    Each Party will at all times comply with legal requirements of the Australian Privacy Principles as
                    set out in the Privacy Act 1988 (Cth) and any privacy or anti-spam Laws applicable in respect of all
                    Personal Information collected, used, stored or otherwise dealt with under or in connection with
                    these Terms.
                  </li>
                  <li>
                    All Personal Information received by us in connection with your use of the Resale Royalty Platform
                    and these Terms will be deemed to have been disclosed to us by you. Without limiting this clause 6,
                    you may only disclose Personal Information to us if you are authorised by Law to collect the
                    Personal Information and to use or disclose it in the manner required.
                  </li>
                  <li>This clause 6 will survive the expiry or termination of these Terms.</li>
                </ol>
              </li>
              <li>
                <span className="b3 bold">Our Liability</span>
                <ol className={classNames(styles["sub-list"])}>
                  <li>
                    Despite anything to the contrary, to the maximum extent permitted by law:
                    <ol className={classNames(styles["sub-sub-list"])}>
                      <li>we will not be liable for any Consequential Loss;</li>
                      <li>
                        a Party’s liability for any Liability under these Terms will be reduced proportionately to the
                        extent the relevant Liability was caused or contributed to by the acts or omissions of the other
                        Party (or any of its Personnel), including any failure to mitigate that Liability; and
                      </li>
                      <li>
                        our aggregate liability for any Liability arising from or in connection with these Terms,
                        including your use of the Resale Royalty Platform, will be limited to $1.
                      </li>
                    </ol>
                  </li>
                  <li>
                    To the maximum extent permitted by law, you indemnify and continue to indemnify us against all
                    Liability we suffer or incur arising from or as a consequence of a breach of clause 4.
                  </li>
                </ol>
              </li>
              <li>
                You acknowledge and agree that we may pursue any available equitable or other remedy against you if you
                breach any provision of these Terms.
                <ol className={classNames(styles["sub-list"])}>
                  <li>
                    We warrant that we have made best endeavours to ensure that the information which we have sourced
                    and placed in the Resale Royalty Platform is accurate and appropriate for its purpose, otherwise we
                    exclude all express and implied warranties, representations and guarantees of any kind (whether
                    under statute, law, equity or on any other basis) and all materials, work, goods and services
                    (including the Resale Royalty Platform) are provided to you without warranties, representations and
                    guarantees of any kind, unless expressly stipulated in these Terms.
                  </li>
                  <li>This clause 7 will survive the expiry or termination of these Terms.</li>
                </ol>
              </li>
              <li>
                <span className="b3 bold">General</span>
                <ol className={classNames(styles["sub-list"])}>
                  <li>
                    We reserve the right at any time and from time to time to change or remove features of, and data in,
                    the Resale Royalty Platform.
                  </li>
                  <li>
                    Any notice given under these Terms must be in writing addressed to the relevant address last
                    notified by the recipient to the Parties. Any notice may be sent by standard post or email, and will
                    be deemed to have been served on the expiry of 2 Business Days in the case of post, or at the time
                    of transmission in the case of transmission by email (or, where the time of transmission is not on a
                    Business Day, 9am on the next Business Day).
                  </li>
                  <li>
                    Any failure or delay by a Party in exercising a power or right (either wholly or partly) in relation
                    to these Terms does not operate as a waiver or prevent a Party from exercising that power or right
                    or any other power or right. A waiver must be in writing.
                  </li>
                  <li>
                    If a provision of these Terms is held to be void, invalid, illegal or unenforceable, that provision
                    is to be read down as narrowly as necessary to allow it to be valid or enforceable, failing which,
                    that provision (or that part of that provision) will be severed from these Terms without affecting
                    the validity or enforceability of the remainder of that provision or the other provisions.
                  </li>
                  <li>
                    We may assign, transfer, novate or otherwise deal with all or any of our rights or obligations under
                    these Terms without your prior written consent. You may not assign, transfer or otherwise deal with
                    all or any of your rights or obligations under these Terms without our prior written consent. Any
                    purported dealing in breach of this clause by you is of no force or effect.
                  </li>
                  <li>
                    We may vary these Terms by providing you with notice of the varied terms when you log in to the
                    Resale Royalty Platform and asking you to accept those terms.
                  </li>
                  <li>
                    These Terms are governed by the laws of New South Wales, Australia. You irrevocably and
                    unconditionally submit to the exclusive jurisdiction of the courts operating in New South Wales and
                    any courts entitled to hear appeals from those courts and waive any rights to object to proceedings
                    being brought in those courts. This clause 9.7 will survive the expiry or termination of these
                    Terms.
                  </li>
                </ol>
              </li>

              <li className={classNames(styles["definitions-list"])}>
                <span className="b3 bold">Definitions</span>
                <br />
                <br />
                <div>Capitalised words have the meaning given in text or as set out below.</div>
                <div>
                  <span className="b3 bold">Business Day </span>means a day which is not a Saturday, Sunday or bank or
                  public holiday in New South Wales, Australia;
                </div>
                <div>
                  <span className="b3 bold">Computing Environment </span>means your computing environment including all
                  hardware, software, information technology and telecommunications services and Systems;
                </div>
                <div>
                  <span className="b3 bold">Consequential Loss </span>includes any consequential loss, indirect loss,
                  real or anticipated loss of profit, loss of benefit, loss of revenue, loss of business, loss of
                  goodwill, loss of opportunity, loss of savings, loss of reputation, loss of use and/or loss or
                  corruption of data, whether under statute, contract, equity, tort (including negligence), indemnity or
                  otherwise;
                </div>
                <div>
                  <span className="b3 bold">Force Majeure Event </span>means an event which is beyond our reasonable
                  control (including but not limited to fire, storm, flood, earthquake, explosion, accident, act of the
                  public enemy, terrorist act, war, rebellion, insurrection, sabotage, epidemic, pandemic, Government
                  sanctioned restrictions and orders, transportation embargo, and strike by employees of a third person
                  whether known or unknown at the time of entering into these Terms);
                </div>
                <div>
                  <span className="b3 bold">Improvements </span>means any development, modification, adaptation or
                  improvement of Our Materials or any New Materials made by or on behalf of either Party, or in respect
                  of which Intellectual Property Rights are acquired by, either Party during the Term;
                </div>
                <div>
                  <span className="b3 bold">Intellectual Property </span>means any copyright, registered and
                  unregistered trademarks, designs (whether or not registered or registrable), domain names, know-how,
                  inventions, processes, trade secrets or confidential information; or circuit layouts, software,
                  computer programs, databases or source codes, including any application, or right to apply, for
                  registration of, and any improvements, enhancements or modifications of, the foregoing;
                </div>
                <div>
                  <span className="b3 bold">Intellectual Property Rights </span>means for the duration of the rights in
                  any part of the world, any industrial or intellectual property rights, whether registrable or not,
                  including in respect of Intellectual Property;
                </div>
                <div>
                  <span className="b3 bold">Laws </span>means all applicable laws, regulations, codes, guidelines,
                  policies, protocols, consents, approvals, permits and licences, and any requirements or directions
                  given by any person with the authority to bind the relevant Party in connection with these Terms or
                  the provision of the Resale Royalty Platform, and includes the Resale Royalty for Visual Artists Act
                  2009 (Cth) and the Privacy Act 1988 (Cth);
                </div>
                <div>
                  <span className="b3 bold">Liability </span>means any expense, cost, liability, loss, damage, claim,
                  notice, entitlement, investigation, demand, proceeding or judgment (whether under statute, contract,
                  equity, tort (including negligence), indemnity or otherwise), howsoever arising, whether direct or
                  indirect and/or whether present, unascertained, future or contingent and whether involving a third
                  party or a Party to these Terms or otherwise;
                </div>
                <div>
                  <span className="b3 bold">New Materials </span>means all Intellectual Property developed, adapted,
                  modified or created by or on behalf of us or you or any of your or our respective Personnel, in
                  connection with the Resale Royalty Platform (including any machine learning algorithms output from the
                  Resale Royalty Platform), whether before or after the date of these Terms;
                </div>
                <div>
                  <span className="b3 bold">Our Materials </span>means all data, work, models, processes, technologies,
                  strategies, materials, information, documentation and services (including our Intellectual Property
                  and the Resale Royalty Platform) generated by the Resale Royalty Platform as a result of your, or your
                  Personnel’s use of the Resale Royalty Platform or that we may provide to you under these Terms, and
                  which may contain material which is owned by or licensed to us, and is protected by Australian and
                  international laws;
                </div>
                <div>
                  <span className="b3 bold">Party </span>means a party to these Terms;
                </div>
                <div>
                  <span className="b3 bold">Personal Information </span>is defined in the Privacy Act 1988 (Cth) and
                  also includes any similar term as defined in any other applicable privacy law;
                </div>
                <div>
                  <span className="b3 bold">Personnel </span>means in respect of a Party, any of its employees,
                  consultants, suppliers, subcontractors or agents;
                </div>
                <div>
                  <span className="b3 bold">Resale Royalty Scheme </span>means the resale royalty for visual artists
                  scheme established by the Commonwealth Government of Australia under the Resale Royalty for Visual
                  Artists Act 2009 (Cth) and administered by Copyright Agency Limited;
                </div>
                <div>
                  <span className="b3 bold">System </span>means all hardware, software, networks and other IT systems
                  used by a Party from time to time, including a network; and
                </div>
                <div>
                  <span className="b3 bold">Third Party Claims </span>means any allegation or claim by a third party
                  that the Resale Royalty Platform or any information available on or from it, or your use of that
                  platform or information, contravenes any Law or their rights.
                </div>
                <div>
                  <span className="b3 bold">Third Party Inputs </span>means any:
                  <ol className={classNames(styles["sub-sub-list"])}>
                    <li>
                      third parties including customers, end users, suppliers, transportation or logistics providers or
                      other subcontractors; or
                    </li>
                    <li>software, goods and services provided by third parties, including open source licences,</li>
                    which the provision of the Resale Royalty Platform may be contingent on, or impacted by.
                  </ol>
                </div>
              </li>
            </ol>
          </Row>
        </Row>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-center align-items-center">
        <Button disabled={!isAgreed} onClick={closeModal}>
          I agree & register
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TermsAndConditionsModal;
